import { useMemo, useState } from "react";
import { seoConfig } from "../data/seoConfig";
import SocialIcon from "./SocialIcon";

function encode(value) {
  return encodeURIComponent(value || "");
}

export default function ArticleShareBlock({ title, path, className = "" }) {
  const [copied, setCopied] = useState(false);
  const articleUrl = useMemo(
    () => `${seoConfig.baseUrl}${path || ""}`,
    [path],
  );

  const shareLinks = [
    {
      id: "x",
      label: "Share on X",
      url: `https://twitter.com/intent/tweet?url=${encode(articleUrl)}&text=${encode(title)}`,
    },
    {
      id: "facebook",
      label: "Share on Facebook",
      url: `https://www.facebook.com/sharer/sharer.php?u=${encode(articleUrl)}`,
    },
    {
      id: "linkedin",
      label: "Share on LinkedIn",
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encode(articleUrl)}`,
    },
    {
      id: "whatsapp",
      label: "Share on WhatsApp",
      url: `https://wa.me/?text=${encode(`${title} ${articleUrl}`)}`,
    },
  ];

  async function copyArticleUrl() {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(articleUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2200);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = articleUrl;
      textArea.setAttribute("readonly", "");
      textArea.style.position = "absolute";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2200);
    }
  }

  return (
    <section
      className={`mt-10 rounded-[1.6rem] border border-neutral-200 bg-white p-5 sm:p-6 ${className}`}
      aria-labelledby="share-article-heading"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2
            id="share-article-heading"
            className="text-sm font-black uppercase tracking-[0.18em] text-neutral-500"
          >
            Share this article
          </h2>
          <p className="mt-2 text-sm leading-6 text-neutral-600">
            Share the current TechniaHQRobot article page.
          </p>
        </div>
        <span
          className="text-sm font-bold text-green-700"
          role="status"
          aria-live="polite"
        >
          {copied ? "Link copied" : ""}
        </span>
      </div>

      <div className="mt-5 grid w-full max-w-sm grid-cols-5 gap-2 overflow-hidden sm:gap-3">
        {shareLinks.map((item) => (
          <a
            key={item.id}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={item.label}
            title={item.label}
            className="focus-ring grid aspect-square w-full max-w-12 min-w-0 place-items-center justify-self-center rounded-full border border-neutral-200 bg-neutral-50 p-0 text-neutral-850 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
          >
            <SocialIcon id={item.id} className="h-5 w-5" />
          </a>
        ))}
        <button
          type="button"
          onClick={copyArticleUrl}
          aria-label="Copy article link"
          title="Copy article link"
          className="focus-ring grid aspect-square w-full max-w-12 min-w-0 place-items-center justify-self-center rounded-full border border-neutral-200 bg-neutral-50 p-0 text-neutral-850 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
        >
          <SocialIcon id="link" className="h-5 w-5" />
        </button>
      </div>
    </section>
  );
}
